'use client'

import { captureException as sentryCaptureException } from '@sentry/nextjs'
import NextError from 'next/error'
import { useEffect } from 'react'
import {
  DEFAULT_LOCALE,
  getLocaleDirection,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@/i18n/locales'

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  useEffect(() => {
    // The original exception may already have been captured or filtered. A
    // distinct event records that it actually replaced the whole application.
    sentryCaptureException(new Error('Global client error boundary rendered', { cause: error }), {
      level: 'fatal',
      tags: { area: 'global-error' },
      fingerprint: ['global-error', '{{ default }}'],
    })

    const browserLocale = navigator.language.trim().split('-')[0]?.toLowerCase()
    const locale = SUPPORTED_LOCALES.includes(browserLocale as SupportedLocale)
      ? (browserLocale as SupportedLocale)
      : DEFAULT_LOCALE
    document.documentElement.lang = locale
    document.documentElement.dir = getLocaleDirection(locale)
  }, [error])

  return (
    <html dir={getLocaleDirection(DEFAULT_LOCALE)} lang={DEFAULT_LOCALE} suppressHydrationWarning>
      <body>
        {/* `NextError` is the default Next.js error page component. Its type
        definition requires a `statusCode` prop. However, since the App Router
        does not expose status codes for errors, we simply pass 0 to render a
        generic error message. */}
        <NextError statusCode={0} />
      </body>
    </html>
  )
}
